import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Button,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { httpClient } from "../httpClient";
import { Order } from "./types";

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  cash: "Bar",
  paypal: "PayPal",
};

function formatPrice(price: number): string {
  return `€ ${price.toFixed(2).replace(".", ",")}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("de-AT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function OrderConfirmationPage() {
  const { token } = useParams<{ token: string }>();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    httpClient(`/api/shop/orders/by-token/${token}`)
      .then(({ json }) => setOrder(json))
      .catch(() => setError("Bestellung nicht gefunden."))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={6}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !order) {
    return (
      <Box p={3}>
        <Alert severity="error">{error ?? "Bestellung nicht gefunden."}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 600, mx: "auto", p: { xs: 1, sm: 2, md: 3 } }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3, color: "success.main" }}>
        <CheckCircleOutlineIcon sx={{ fontSize: 36 }} />
        <Typography variant="h5" fontWeight={700}>
          Bestellung eingegangen
        </Typography>
      </Box>

      <Card>
        <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box
            sx={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}
          >
            <Typography variant="body2" color="text.secondary">
              Bestellnummer: <strong>#{order.id}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {formatDate(order.created_at)}
            </Typography>
          </Box>

          <Divider />

          <Typography variant="subtitle2" color="text.secondary">
            Bestellt von
          </Typography>
          <Typography fontWeight={500}>{order.customer_name}</Typography>
          {order.customer_email && (
            <Typography variant="body2" color="text.secondary">
              {order.customer_email}
            </Typography>
          )}

          {order.notes && (
            <>
              <Divider />
              <Typography variant="subtitle2" color="text.secondary">
                Anmerkung
              </Typography>
              <Typography variant="body2">{order.notes}</Typography>
            </>
          )}

          <Divider />

          <Typography variant="subtitle2" color="text.secondary">
            Zahlungsart
          </Typography>
          <Typography fontWeight={500}>
            {PAYMENT_METHOD_LABELS[order.payment_method] ?? order.payment_method}
          </Typography>
          {order.payment_method === "paypal" && order.paypal_link && (
            <>
              <Typography variant="body2" color="text.secondary">
                Bitte gib <strong>#{order.order_number ?? order.id}</strong> als Verwendungszweck an, damit wir deine Zahlung zuordnen können.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                href={order.paypal_link}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ alignSelf: "flex-start" }}
              >
                Jetzt mit PayPal bezahlen
              </Button>
            </>
          )}

          <Divider />

          <Typography variant="subtitle2" color="text.secondary">
            Artikel
          </Typography>

          {order.items.map((item, i) => (
            <Box
              key={i}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 1,
              }}
            >
              <Box>
                <Typography variant="body2" fontWeight={500}>
                  {item.shop_item_name} × {item.quantity}
                </Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" mt={0.25}>
                  {item.size && <Chip label={`Größe: ${item.size}`} size="small" />}
                  {item.imprint && (
                    <Chip label={`Aufdruck: ${item.imprint}`} size="small" color="info" />
                  )}
                </Stack>
              </Box>
              <Typography variant="body2" fontWeight={500} whiteSpace="nowrap">
                {formatPrice(item.subtotal)}
              </Typography>
            </Box>
          ))}

          <Divider />

          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="subtitle1" fontWeight={700}>
              Gesamt
            </Typography>
            <Typography variant="subtitle1" fontWeight={700} color="primary">
              {formatPrice(order.total)}
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Typography
        variant="caption"
        color="text.secondary"
        mt={2}
        display="block"
        textAlign="center"
      >
        Diese Seite kann jederzeit über diesen Link aufgerufen werden.
      </Typography>
    </Box>
  );
}
